<template>
  <div v-if="normalizedScore" class="eco-score-badge">
    <v-chip
      v-if="compact"
      class="eco-score-chip"
      size="small"
      variant="flat"
      label
      :style="chipStyle"
    >
      {{ normalizedScore }} · −{{ savedKg }} kg CO2
    </v-chip>

    <div v-else class="eco-score-card">
      <div class="eco-score-card__brand">
        <img
          src="@/assets/eco-repair-score-logo.png"
          alt="Eco Repair Score"
          class="eco-score-card__logo"
        />
      </div>

      <div class="eco-score-card__divider" />

      <div class="eco-score-card__body">
        <div class="eco-score-card__headline">
          <span class="eco-score-card__result" :style="resultStyle">{{
            normalizedScore
          }}</span>
          <span>−{{ savedKg }} kg CO2</span>
        </div>
        <div class="eco-score-card__compare">
          <span>{{ $t("ecoScore.vsNew") }}</span>
          <span class="eco-score-card__figures">
            ({{ formattedSecondHand }} vs {{ formattedNew }} kg)
          </span>
        </div>
        <div class="eco-score-card__method">
          {{ $t("ecoScore.calculatedWith") }}
          <a :href="methodologyUrl" target="_blank" rel="noopener noreferrer">{{
            $t("ecoScore.brand")
          }}</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const LETTERS = [
  { id: "A", color: "#1D9E75", text: "#146B50" },
  { id: "B", color: "#97C459", text: "#4E7A1A" },
  { id: "C", color: "#EF9F27", text: "#8A5A08" },
  { id: "D", color: "#D85A30", text: "#A33E1C" },
  { id: "E", color: "#A32D2D", text: "#A32D2D" },
];

function hexToRgba(hex, alpha) {
  const value = hex.replace("#", "");
  const red = parseInt(value.slice(0, 2), 16);
  const green = parseInt(value.slice(2, 4), 16);
  const blue = parseInt(value.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export default {
  name: "EcoScoreBadge",
  props: {
    score: {
      type: String,
      default: "",
    },
    secondHandKg: {
      type: Number,
      default: null,
    },
    newKg: {
      type: Number,
      default: null,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    methodologyUrl: {
      type: String,
      default: "https://methodology.ecorepairscore.com/public",
    },
  },
  data() {
    return { letters: LETTERS };
  },
  computed: {
    normalizedScore() {
      return String(this.score || "")
        .trim()
        .toUpperCase();
    },
    activeLetter() {
      return (
        this.letters.find((letter) => letter.id === this.normalizedScore) ||
        null
      );
    },
    savedKg() {
      const fresh = Number(this.newKg);
      const used = Number(this.secondHandKg);
      if (!Number.isFinite(fresh) || !Number.isFinite(used)) return 0;
      return Math.round(fresh - used);
    },
    formattedSecondHand() {
      return this.formatKg(this.secondHandKg);
    },
    formattedNew() {
      return this.formatKg(this.newKg);
    },
    chipStyle() {
      const letter = this.activeLetter || this.letters[0];
      return {
        backgroundColor: hexToRgba(letter.color, 0.16),
        color: letter.text,
      };
    },
    resultStyle() {
      const letter = this.activeLetter || this.letters[0];
      return {
        backgroundColor: letter.color,
        color: "#fff",
      };
    },
  },
  methods: {
    formatKg(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return "";
      const fromStore =
        this.$store && this.$store.getters && this.$store.getters.language;
      const fromI18n = this.$i18n && this.$i18n.locale;
      const locale =
        (typeof fromStore === "string" && fromStore) ||
        (typeof fromI18n === "string" && fromI18n) ||
        (fromI18n && fromI18n.value) ||
        "en";
      return number.toLocaleString(locale, { maximumFractionDigits: 1 });
    },
  },
};
</script>

<style scoped>
.eco-score-card {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #ece7df;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.eco-score-card__brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.eco-score-card__logo {
  width: 120px;
  height: auto;
  display: block;
}

.eco-score-card__divider {
  width: 1px;
  align-self: stretch;
  margin: 0 16px;
  background: #e6e1da;
}

.eco-score-card__body {
  min-width: 0;
  flex: 1 1 auto;
}

.eco-score-card__headline {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: #1c1b19;
}

.eco-score-card__result {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
}

.eco-score-card__compare {
  display: flex;
  flex-wrap: wrap;
  column-gap: 4px;
  margin-top: 2px;
  font-size: 14px;
  line-height: 1.3;
  color: #3c4043;
}

.eco-score-card__figures {
  white-space: nowrap;
}

.eco-score-card__method {
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.3;
  color: #9aa0a6;
}

.eco-score-card__method a {
  color: #6b7280;
  text-decoration: none;
}

.eco-score-card__method a:hover {
  text-decoration: underline;
}

.eco-score-chip {
  font-weight: 700;
}
</style>
